import React from "react";
import { Typography } from "@/components/ui/Typography/Typography";
import SelectDropdown, {
  TSelectOption,
} from "@/components/forms/Select/Select";
import {
  THierarchyPlacementMode,
  TRoleHierarchyOrderItem,
  TRoleHierarchyPreview,
} from "@/api/role-management/role-management.types";
import { useTranslation } from "react-i18next";

type TRoleHierarchyPreviewPanelProps = {
  preview?: TRoleHierarchyPreview | null;
  isLoading?: boolean;
};

const dropdownFieldClassName = "space-y-2 w-full sm:w-72";

const labelClassName =
  "block text-sm font-medium text-gray-700 dark:text-muted-foreground";

const RoleOrderList: React.FC<{
  title: string;
  roles: TRoleHierarchyOrderItem[];
}> = ({ title, roles }) => (
  <div className="space-y-2">
    <Typography
      variant="small"
      affects="medium"
      className="text-muted-foreground"
    >
      {title}
    </Typography>
    <ul className="space-y-1 rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-background">
      {roles.map((role) => (
        <li
          key={role.internal_id}
          className="flex items-center justify-between text-sm text-foreground"
        >
          <span>{role.name}</span>
          <span className="text-muted-foreground text-xs">
            {role.rank ?? "—"}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

export const RoleHierarchyPreviewPanel: React.FC<
  TRoleHierarchyPreviewPanelProps
> = ({ preview, isLoading }) => {
  const { t } = useTranslation(["roleManagementPage"]);

  if (isLoading) {
    return (
      <Typography variant="small" className="text-muted-foreground">
        {t("ROLES.HIERARCHY.PREVIEW_LOADING")}
      </Typography>
    );
  }

  if (!preview) {
    return null;
  }

  return (
    <div className="space-y-4 rounded-md border border-blue-200 dark:border-gray-700 bg-blue-50/60 dark:bg-background p-4">
      <Typography
        variant="small"
        affects="medium"
        className="text-blue-900 dark:text-foreground"
      >
        {t("ROLES.HIERARCHY.PREVIEW_TITLE")}
      </Typography>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RoleOrderList
          title={t("ROLES.HIERARCHY.BEFORE")}
          roles={preview.before_order}
        />
        <RoleOrderList
          title={t("ROLES.HIERARCHY.AFTER")}
          roles={preview.after_order}
        />
      </div>
      {preview.invalid_relationship_count > 0 && (
        <Typography
          variant="small"
          className="text-amber-700 dark:text-amber-400"
        >
          {t("ROLES.HIERARCHY.IMPACT_WARNING", {
            count: preview.invalid_relationship_count,
            employees: preview.affected_employee_count,
          })}
        </Typography>
      )}
    </div>
  );
};

export type TPlacementFormValues = {
  placement_mode: THierarchyPlacementMode | "";
  reference_role_id: string;
  secondary_reference_role_id?: string;
};

type TRoleHierarchyPlacementFieldsProps = {
  roles: {
    internal_id: string;
    name: string;
    rank?: number | null;
    is_active?: boolean;
  }[];
  values: TPlacementFormValues;
  onChange: (values: Partial<TPlacementFormValues>) => void;
  excludeRoleId?: string;
  rolesLoading?: boolean;
  errors?: Record<string, string>;
  activeToggle?: React.ReactNode;
};

const PLACEMENT_MODES: THierarchyPlacementMode[] = [
  "above",
  "below",
  "between",
  "peer",
];

const PLACEMENT_MODE_LABEL_KEYS = {
  above: "ROLES.HIERARCHY.MODE_ABOVE",
  below: "ROLES.HIERARCHY.MODE_BELOW",
  between: "ROLES.HIERARCHY.MODE_BETWEEN",
  peer: "ROLES.HIERARCHY.MODE_PEER",
} as const satisfies Record<
  THierarchyPlacementMode,
  | "ROLES.HIERARCHY.MODE_ABOVE"
  | "ROLES.HIERARCHY.MODE_BELOW"
  | "ROLES.HIERARCHY.MODE_BETWEEN"
  | "ROLES.HIERARCHY.MODE_PEER"
>;

const sortRolesByRankAsc = (
  roles: { internal_id: string; name: string; rank?: number | null }[],
) =>
  [...roles].sort((a, b) => {
    const rankA = a.rank;
    const rankB = b.rank;
    if (rankA == null && rankB == null) {
      return a.name.localeCompare(b.name);
    }
    if (rankA == null) {
      return 1;
    }
    if (rankB == null) {
      return -1;
    }
    if (rankA !== rankB) {
      return rankA - rankB;
    }
    return a.name.localeCompare(b.name);
  });

export const RoleHierarchyPlacementFields: React.FC<
  TRoleHierarchyPlacementFieldsProps
> = ({
  roles,
  values,
  onChange,
  excludeRoleId,
  rolesLoading = false,
  errors,
  activeToggle,
}) => {
  const { t } = useTranslation(["roleManagementPage"]);

  const selectableRoles = React.useMemo(
    () =>
      sortRolesByRankAsc(
        roles.filter(
          (role) =>
            role.is_active !== false && role.internal_id !== excludeRoleId,
        ),
      ),
    [roles, excludeRoleId],
  );

  const isPositionSelected = Boolean(values.placement_mode);
  const isBetweenMode = values.placement_mode === "between";

  const handleModeChange = (mode: string) => {
    onChange({
      placement_mode: mode as THierarchyPlacementMode | "",
      reference_role_id: "",
      secondary_reference_role_id: "",
    });
  };

  const handleReferenceRoleChange = (referenceRoleId: string) => {
    const updates: Partial<TPlacementFormValues> = {
      reference_role_id: referenceRoleId,
    };

    if (
      referenceRoleId &&
      referenceRoleId === values.secondary_reference_role_id
    ) {
      updates.secondary_reference_role_id = "";
    }

    onChange(updates);
  };

  const formatRoleOptionLabel = React.useCallback(
    (role: { internal_id: string; name: string; rank?: number | null }) =>
      `${role.name}${
        role.rank != null ? ` (${t("ROLES.HIERARCHY.LEVEL")} ${role.rank})` : ""
      }`,
    [t],
  );

  const placementModeOptions = React.useMemo(
    (): TSelectOption[] =>
      PLACEMENT_MODES.map((mode) => ({
        value: mode,
        label: t(PLACEMENT_MODE_LABEL_KEYS[mode]),
      })),
    [t],
  );

  const referenceRoleOptions = React.useMemo(
    (): TSelectOption[] =>
      selectableRoles
        .filter(
          (role) => role.internal_id !== values.secondary_reference_role_id,
        )
        .map((role) => ({
          value: role.internal_id,
          label: formatRoleOptionLabel(role),
        })),
    [
      selectableRoles,
      values.secondary_reference_role_id,
      formatRoleOptionLabel,
    ],
  );

  const secondaryReferenceRoleOptions = React.useMemo(
    (): TSelectOption[] =>
      selectableRoles
        .filter((role) => role.internal_id !== values.reference_role_id)
        .map((role) => ({
          value: role.internal_id,
          label: formatRoleOptionLabel(role),
        })),
    [selectableRoles, values.reference_role_id, formatRoleOptionLabel],
  );

  const selectedPlacementMode =
    placementModeOptions.find(
      (option) => option.value === values.placement_mode,
    ) ?? null;

  const selectedReferenceRole =
    referenceRoleOptions.find(
      (option) => option.value === values.reference_role_id,
    ) ?? null;

  const selectedSecondaryReferenceRole =
    secondaryReferenceRoleOptions.find(
      (option) => option.value === values.secondary_reference_role_id,
    ) ?? null;

  return (
    <div className="space-y-4 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-background p-4">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
        <div className={dropdownFieldClassName}>
          <label
            id="placement_mode"
            htmlFor="select-placement_mode"
            className={labelClassName}
          >
            {t("ROLES.HIERARCHY.POSITION_TITLE")}
          </label>
          <SelectDropdown
            name="placement_mode"
            labelBy="placement_mode"
            placeholder={t("ROLES.HIERARCHY.SELECT_POSITION")}
            options={placementModeOptions}
            value={selectedPlacementMode}
            onChange={(option: TSelectOption | null) =>
              handleModeChange(option?.value ?? "")
            }
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            isClearable={false}
            isSearchable
            className={
              errors?.placement_mode
                ? "ring-1 ring-red-500 rounded-md"
                : undefined
            }
          />
          {errors?.placement_mode && (
            <Typography variant="small" className="text-red-600">
              {errors.placement_mode}
            </Typography>
          )}
        </div>

        {activeToggle && (
          <div className="space-y-2 shrink-0">{activeToggle}</div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:gap-6">
        <div className={dropdownFieldClassName}>
          <label
            id="reference_role_id"
            htmlFor="select-reference_role_id"
            className={labelClassName}
          >
            {t("ROLES.HIERARCHY.REFERENCE_ROLE")}
          </label>
          <SelectDropdown
            name="reference_role_id"
            labelBy="reference_role_id"
            placeholder={t("ROLES.HIERARCHY.SELECT_REFERENCE")}
            options={referenceRoleOptions}
            value={selectedReferenceRole}
            onChange={(option: TSelectOption | null) =>
              handleReferenceRoleChange(option?.value ?? "")
            }
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            isDisabled={!isPositionSelected || rolesLoading}
            isLoading={rolesLoading}
            isClearable={false}
            isSearchable
            className={
              errors?.reference_role_id
                ? "ring-1 ring-red-500 rounded-md"
                : undefined
            }
          />
          {errors?.reference_role_id && (
            <Typography variant="small" className="text-red-600">
              {errors.reference_role_id}
            </Typography>
          )}
        </div>

        {isBetweenMode && (
          <div className={dropdownFieldClassName}>
            <label
              id="secondary_reference_role_id"
              htmlFor="select-secondary_reference_role_id"
              className={labelClassName}
            >
              {t("ROLES.HIERARCHY.SECONDARY_REFERENCE_ROLE")}
            </label>
            <SelectDropdown
              name="secondary_reference_role_id"
              labelBy="secondary_reference_role_id"
              placeholder={t("ROLES.HIERARCHY.SELECT_REFERENCE")}
              options={secondaryReferenceRoleOptions}
              value={selectedSecondaryReferenceRole}
              onChange={(option: TSelectOption | null) =>
                onChange({ secondary_reference_role_id: option?.value ?? "" })
              }
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              isDisabled={!values.reference_role_id || rolesLoading}
              isLoading={rolesLoading}
              isClearable={false}
              isSearchable
              className={
                errors?.secondary_reference_role_id
                  ? "ring-1 ring-red-500 rounded-md"
                  : undefined
              }
            />
            {errors?.secondary_reference_role_id && (
              <Typography variant="small" className="text-red-600">
                {errors.secondary_reference_role_id}
              </Typography>
            )}
          </div>
        )}
      </div>

      <Typography
        variant="small"
        className="text-sm text-muted-foreground max-w-2xl"
      >
        {t("ROLES.HIERARCHY.AUTO_RANK_HINT")}
      </Typography>
    </div>
  );
};
